import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import API_BASE_URL from '../config/api';
import Pagination from '../components/Pagination';
import { usePageSize } from '../hooks/usePagination';
import './Wishlists.css';

const ENTITY_TYPES = ['', 'Hotel', 'Spa', 'Tour', 'TravelPackage'];
const STATUSES = ['', 'open', 'reviewing', 'resolved', 'rejected'];
const REASONS = [
  '',
  'Wrong Listing',
  'Wrong Contact details',
  'Wrong Location',
  'Wrong Building / Images',
  'Any Other reason'
];

const formatDateTime = (value) => {
  try {
    if (!value) return '';
    return new Date(value).toLocaleString();
  } catch {
    return String(value || '');
  }
};

const getEntityLabel = (r) => {
  const name = r?.entitySnapshot?.name || '';
  const title = r?.entitySnapshot?.title || '';
  return name || title || '(unknown)';
};

const Reports = () => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // ?status=open (from the dashboard) opens the list already filtered
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState(() => (STATUSES.includes(searchParams.get('status')) ? searchParams.get('status') : ''));
  const [entityType, setEntityType] = useState('');
  const [reason, setReason] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [pageSize, setPageSize] = usePageSize();

  const [selected, setSelected] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [adminNotes, setAdminNotes] = useState('');

  const queryParams = useMemo(() => {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    if (entityType) params.set('entityType', entityType);
    if (reason) params.set('reason', reason);
    if (q) params.set('q', q);
    params.set('page', String(page));
    params.set('limit', String(pageSize));
    return params.toString();
  }, [status, entityType, reason, q, page, pageSize]);

  const fetchReports = async () => {
    try {
      setLoading(true);
      setError('');
      const res = await axios.get(`${API_BASE_URL}/admin/reports?${queryParams}`);
      if (res.data?.success) {
        setItems(res.data.data || []);
        setTotal(res.data.total || 0);
        setPages(res.data.pages || 1);
      } else {
        setError(res.data?.message || 'Failed to load reports');
      }
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams]);

  const openDetails = (r) => {
    setSelected(r);
    setSelectedStatus(r.status || '');
    setAdminNotes(r.adminNotes || '');
  };

  const closeDetails = () => {
    setSelected(null);
    setSelectedStatus('');
    setAdminNotes('');
  };

  const saveDetails = async () => {
    if (!selected?._id) return;
    try {
      setUpdating(true);
      const res = await axios.put(`${API_BASE_URL}/admin/reports/${selected._id}`, {
        status: selectedStatus,
        adminNotes
      });
      if (res.data?.success) {
        closeDetails();
        await fetchReports();
      } else {
        alert(res.data?.message || 'Failed to update report');
      }
    } catch (e) {
      alert(e?.response?.data?.message || e.message || 'Failed to update report');
    } finally {
      setUpdating(false);
    }
  };

  return (
    <Layout>
      <div className="container">
        <div className="wishlists-header">
          <div>
            <h1>Reports</h1>
            <p style={{ color: '#64748b', marginTop: 6 }}>User-submitted issues for listings.</p>
          </div>
          <div className="header-stats">
            <span className="stat-badge">Total: {total}</span>
            <span className="stat-badge">Showing: {items.length}</span>
          </div>
        </div>

        <div className="customers-filters">
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
            <select
              className="filter-select"
              value={status}
              onChange={(e) => {
                setPage(1);
                setStatus(e.target.value);
              }}
              title="Status"
            >
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s || 'All Status'}
                </option>
              ))}
            </select>

            <select
              className="filter-select"
              value={entityType}
              onChange={(e) => {
                setPage(1);
                setEntityType(e.target.value);
              }}
              title="Entity Type"
            >
              {ENTITY_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t || 'All Types'}
                </option>
              ))}
            </select>

            <select
              className="filter-select"
              value={reason}
              onChange={(e) => {
                setPage(1);
                setReason(e.target.value);
              }}
              title="Reason"
            >
              {REASONS.map((r) => (
                <option key={r} value={r}>
                  {r || 'All Reasons'}
                </option>
              ))}
            </select>
          </div>

          <div className="search-box">
            <input
              className="search-input"
              value={q}
              onChange={(e) => {
                setPage(1);
                setQ(e.target.value);
              }}
              placeholder="Search listing / city / reporter…"
            />
          </div>
        </div>

        {error && (
          <div className="error-message" style={{ marginBottom: 12 }}>
            {error}
          </div>
        )}

        {(!loading && items.length === 0) ? (
          <div className="empty-state">
            <span className="empty-state-icon">🚩</span>
            <h2>No reports found</h2>
            <p>Try adjusting filters or search.</p>
          </div>
        ) : (
          <div className="wishlists-table-container">
            <table className="wishlists-table">
            <thead>
              <tr>
                <th>Created</th>
                <th>Status</th>
                <th>Type</th>
                <th>Listing</th>
                <th>Reason</th>
                <th>Reporter</th>
                <th>City</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8}>Loading…</td></tr>
              ) : (
                items.map((r) => (
                  <tr key={r._id}>
                    <td>{formatDateTime(r.createdAt)}</td>
                    <td>{r.status}</td>
                    <td>{r.entityType}</td>
                    <td>{getEntityLabel(r)}</td>
                    <td>{r.reason}{r.reason === 'Any Other reason' && r.otherReason ? `: ${r.otherReason}` : ''}</td>
                    <td>{r.reporter?.name || r.reporter?.phone || r.reporter?.email || ''}</td>
                    <td>{r.entitySnapshot?.city || ''}</td>
                    <td style={{ textAlign: 'right' }}>
                      <button className="btn btn-secondary" onClick={() => openDetails(r)}>
                        View
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            </table>
          </div>
        )}

        <Pagination
          page={page}
          pages={pages}
          total={total}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
          loading={loading}
          noun="reports"
        />

        {selected && (
          <div className="customers-modal" onClick={closeDetails} role="presentation">
            <div className="customers-modal-content" onClick={(e) => e.stopPropagation()} role="presentation" style={{ maxWidth: 720 }}>
              <div className="customers-modal-header">
                <h2>Report Details</h2>
                <button className="modal-close" onClick={closeDetails}>×</button>
              </div>

              <div className="customers-modal-body">
                <div className="customers-table-container" style={{ marginBottom: 16 }}>
                  <table className="customers-table">
                    <thead>
                      <tr>
                        <th style={{ width: 180 }}>Field</th>
                        <th>Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td><strong>Created</strong></td>
                        <td>{formatDateTime(selected.createdAt)}</td>
                      </tr>
                      <tr>
                        <td><strong>Status</strong></td>
                        <td>{selected.status}</td>
                      </tr>
                      <tr>
                        <td><strong>Entity Type</strong></td>
                        <td>{selected.entityType}</td>
                      </tr>
                      <tr>
                        <td><strong>Listing</strong></td>
                        <td>{getEntityLabel(selected)}</td>
                      </tr>
                      <tr>
                        <td><strong>City</strong></td>
                        <td>{selected.entitySnapshot?.city || '—'}</td>
                      </tr>
                      <tr>
                        <td><strong>Reason</strong></td>
                        <td>
                          {selected.reason}
                          {selected.reason === 'Any Other reason' && selected.otherReason ? `: ${selected.otherReason}` : ''}
                        </td>
                      </tr>
                      <tr>
                        <td><strong>Details</strong></td>
                        <td style={{ whiteSpace: 'pre-wrap' }}>{selected.details || '—'}</td>
                      </tr>
                      <tr>
                        <td><strong>Reporter</strong></td>
                        <td>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            <span>{selected.reporter?.name || '—'}</span>
                            <span>{selected.reporter?.phone || '—'}</span>
                            <span>{selected.reporter?.email || '—'}</span>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 10 }}>
                  <div>
                    <label style={{ display: 'block', fontWeight: 700, fontSize: 12, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>
                      Update status
                    </label>
                    <select
                      className="filter-select"
                      value={selectedStatus}
                      onChange={(e) => setSelectedStatus(e.target.value)}
                    >
                      {STATUSES.filter(Boolean).map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontWeight: 700, fontSize: 12, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>
                      Admin notes
                    </label>
                    <textarea
                      className="search-input"
                      value={adminNotes}
                      onChange={(e) => setAdminNotes(e.target.value)}
                      rows={4}
                      placeholder="Add notes for your team…"
                    />
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: 24, paddingTop: 0 }}>
                <button className="btn btn-secondary" onClick={closeDetails} disabled={updating}>Cancel</button>
                <button className="btn btn-primary" onClick={saveDetails} disabled={updating}>
                  {updating ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Reports;

